import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, MapPin, Clock, DollarSign, Users, Eye, Filter, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import { useCurrency } from '../components/CurrencyProvider';
import axios from 'axios';

export default function CorporateJobsDetail() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterStatus = searchParams.get('status') || 'all';
  const { format: formatCurrency } = useCurrency();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(filterStatus);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const profileRes = await axios.get(`${API}/corporates/me`, { headers: { Authorization: `Bearer ${token}` } });
        const jobsRes = await axios.get(`${API}/jobs/corporate/${profileRes.data.id}`);
        setJobs(jobsRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchJobs();
  }, [token]);

  const filtered = jobs.filter(j => {
    if (statusFilter !== 'all' && j.status !== statusFilter) return false;
    if (searchTerm && !j.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const statusCounts = {
    all: jobs.length,
    open: jobs.filter(j => j.status === 'open').length,
    closed: jobs.filter(j => j.status === 'closed').length,
    in_progress: jobs.filter(j => j.status === 'in_progress').length,
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/corporate/dashboard')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-slate-900" data-testid="page-title">Training Requirements</h1>
          <p className="text-slate-500 mt-1">All your posted training requirements in one place</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Posted', count: statusCounts.all, color: 'blue', filter: 'all' },
            { label: 'Open', count: statusCounts.open, color: 'emerald', filter: 'open' },
            { label: 'In Progress', count: statusCounts.in_progress, color: 'amber', filter: 'in_progress' },
            { label: 'Closed', count: statusCounts.closed, color: 'slate', filter: 'closed' },
          ].map(c => (
            <div
              key={c.filter}
              onClick={() => setStatusFilter(c.filter)}
              className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${statusFilter === c.filter ? `border-${c.color}-400 ring-2 ring-${c.color}-100` : 'border-slate-200 hover:border-slate-300'}`}
              data-testid={`filter-${c.filter}`}
            >
              <span className="text-sm text-slate-500">{c.label}</span>
              <div className="font-outfit text-2xl font-bold text-slate-900 mt-1">{c.count}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search requirements..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-jobs" />
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Title</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Location</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Mode</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Budget</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Applications</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Posted</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="p-12 text-center text-slate-500">No requirements found</td></tr>
                ) : filtered.map(job => (
                  <tr key={job.id} className="hover:bg-slate-50 transition-colors" data-testid={`job-row-${job.id}`}>
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{job.title}</div>
                      <div className="flex gap-1 mt-1">{(job.skills_required || []).slice(0, 3).map(s => <span key={s} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{s}</span>)}</div>
                    </td>
                    <td className="p-4 text-sm text-slate-600"><MapPin className="w-3 h-3 inline mr-1" />{job.location}</td>
                    <td className="p-4"><span className={`px-2 py-1 text-xs rounded-full font-medium ${job.training_mode === 'on-site' ? 'bg-emerald-50 text-emerald-700' : job.training_mode === 'online' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>{job.training_mode}</span></td>
                    <td className="p-4 text-sm font-medium text-slate-900">{formatCurrency(job.budget)}</td>
                    <td className="p-4"><span className="inline-flex items-center gap-1 text-sm text-slate-600"><Users className="w-3 h-3" />{job.applications_count || 0}</span></td>
                    <td className="p-4"><span className={`px-2 py-1 text-xs rounded-full font-medium ${job.status === 'open' ? 'bg-emerald-50 text-emerald-700' : job.status === 'in_progress' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{job.status}</span></td>
                    <td className="p-4 text-sm text-slate-500">{new Date(job.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/job/${job.id}`)} data-testid={`view-job-${job.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t text-xs text-slate-500">Showing {filtered.length} of {jobs.length} requirements</div>
        </div>
      </div>
    </div>
  );
}
